import {DataType, ApiClients} from '@salesforce/commerce-sdk-react/hooks/types'
type ClientProduct = ApiClients['shopperProducts']

export type ProductType = DataType<ClientProduct['getProduct']>

export enum ProductListingVisualization {
    PRODUCTS = 0,
    STORIES = 1,
}

type VariationAttribute = {
    code?: string
    label?: string
    colorHexCode?: string
}

export type ProductAnalyticsPageGrouping = {
    pageCategory?: string
    pageSubCategory?: string
    pageDetail?: string
}

type ProductAnalytics = {
    categoryPath?: string
    // Canonical category list (for example, "category-man-ready_to_wear-sweaters"),
    // matching PLP category.c_analyticsList.
    list?: string
    // Present only on c_pdp=true responses; always English and for analytics only.
    callout?: string | null
    color: VariationAttribute
    gender?: string
    inStock?: boolean
    productId?: string
    season?: string
    shortDescription?: string
    size: VariationAttribute
    // Page grouping computed server-side by the SCAPI hook (scoped to c_pdp=true requests).
    // Centralizes the PDP analytics page grouping (SFRA Analytics.getProductPageGrouping),
    // mirroring what getCategory already exposes for the PLP.
    pageGrouping?: ProductAnalyticsPageGrouping
}

type ProductImage = {
    alt?: string
    imageViewType?: string
    link?: string
    title?: string
    url?: string
}

export type ProductImages = {
    cover: ProductImage | null
    horizontal: ProductImage | null
    large: Array<ProductImage> | null
    showCarousel?: boolean | null
    showHover?: boolean | null
}

type ProductVideo = {
    CLDvideoVURL?: string
    CLDvideoHURL?: string
}

export type ProductVideos = {
    hasHorizontalVideo: boolean
    hasVerticalVideo: boolean
    hideVideoPLP: boolean
    videoProvider: string
    videoH: ProductVideo
    videoV: ProductVideo
}

type ProductMaterials = {
    care?: string
    washing?: string
    washingInfo?: string
    showProductFromSolomeo?: boolean
}

type ProductDetails = {
    details?: string
    materials?: string
}

export type ProductVariationGroup = ProductType & {
    ID: string
    analytics: ProductAnalytics
    color: VariationAttribute
    default?: boolean
    details?: ProductDetails
    images: ProductImages
    materials?: ProductMaterials
    notifyMeEnabled?: boolean
    enableDisclaimerShipment?: boolean
    pfas?: boolean
    sizeChartID?: string
    online?: boolean
    pdpUrl?: string
    pricing?: ProductPrice
    selectable?: boolean
    textTag?: string
    model?: string
    variants: Array<ProductVariant>
    videos: ProductVideos
}

export type ProductVariant = {
    ID: string
    analytics: ProductAnalytics
    availability: {
        inStock?: boolean
        isOnlyOneLeft?: boolean
    }
    color: VariationAttribute
    online?: boolean
    pdpUrl?: string
    pricing?: ProductPrice
    selectable?: boolean
    shortDescription?: string
    model?: string
    size: VariationAttribute
}

export type ProductPrice = {
    currency?: string
    discount: {
        price: {value: number; formattedValue: string} | null
        priceRange: {minPrice: {value: number; formattedValue: string}; maxPrice: {value: number; formattedValue: string}} | null
    } | null
    hasDiscount: boolean
    hasPriceRange: boolean
    price: {value: number; formattedValue: string} | null
    priceRange: {minPrice: {value: number; formattedValue: string}; maxPrice: {value: number; formattedValue: string}} | null
}

// A "shop by look" product is a SFCC product `set`. Each entry is the product ID (already
// resolved to its default color) for one look slot; the FE re-fetches full tile data via useProducts.
export type ProductSetData = {
    images: ProductImages
    analytics: ProductAnalytics
    pdpUrl?: string
    textTag?: string | null
    productSetProductsIds: Array<string>
    productSetProducts: Array<FullProductType>
}

export type FullProductType = ProductType & {
    c_ariaLabel?: string
    c_setData?: ProductSetData
    c_analytics: ProductAnalytics
    c_base: {
        ID?: string
        UUID?: string
        gender?: string
        season?: string
        shortDescription?: string
        skuMaster?: string
        type: {
            master?: boolean
            variationGroup?: boolean
            variant?: boolean
            set?: boolean
        }
    }
    c_seo: {
        alternateUrls?: Array<{hreflang?: string; url?: string}>
        canonicalUrl?: string
        description?: string
        schemaBreadcrumbs?: object
        schemaData?: object
        seoNoIndex?: boolean
        title?: string
    }
    c_variationGroupsData: Array<ProductVariationGroup>
    c_newArrivalsUrl: string | null
    c_bannerPageId: string | null
}

export interface ProductImpression {
    name: string
    id: string
    price: string
    category: string
    callout?: string
    variant: string
    tileType: string
    color: string
    gender: string
    list: string
    position?: string
    quantity?: number
    dimension14?: string
    dimension21?: string
    dimension22?: string
    dimension26?: string
    dimension27?: string
    product_sorting_engine?: 'salesforce' | 'dynamic_yield'
    product_sorting_type?: string
}
